import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  SELF_METRICS_INTERVAL_MS,
  type SelfMetricsDto,
  type ServiceMetricsDto,
  type SystemSnapshotDto,
} from '@acs/shared'
import { apiGet } from '@/lib/api'

/**
 * The System view's two data sources.
 *
 * They are two cache entries rather than one because they move at completely
 * different speeds and arrive by different transports:
 *
 *   ['system']       polled, 30s — Railway's per-service series and the plan
 *                    limits. Slow on purpose; Railway's own grid is 30s and its
 *                    freshest sample is 20–60s old, so asking faster returns
 *                    the same numbers.
 *   ['self-metrics'] pushed, 2s — this API process measuring itself. The stream
 *                    is the ONLY writer; there is no fetch behind this key.
 *
 * Both the rail's status chip and the /system route read from here, so the two
 * can never disagree about what the box is doing.
 */

export const SYSTEM_KEY = ['system'] as const

/**
 * Deliberately NOT `['system', 'self']`. React Query matches keys by prefix, so
 * a nested key would make `invalidateQueries(['system'])` — which the reconnect
 * path runs — also invalidate a key that has no query function to refetch with.
 */
export const SELF_METRICS_KEY = ['self-metrics'] as const

/**
 * How many pushed samples to keep. At a 2s tick this is five minutes of live
 * history, which is the span the fast chart draws; Railway's half of the panel
 * covers the longer window.
 */
export const SELF_HISTORY_LIMIT = Math.round((5 * 60_000) / SELF_METRICS_INTERVAL_MS)

/**
 * Railway's series plus a seed self-sample.
 *
 * Polled at Railway's own cadence. The API collapses every console onto one
 * upstream call per 25s, so this interval costs the same whether one tab is
 * open or twelve.
 */
export function useSystemMetrics(): UseQueryResult<SystemSnapshotDto> {
  return useQuery({
    queryKey: SYSTEM_KEY,
    queryFn: ({ signal }) => apiGet<SystemSnapshotDto>('/api/system/metrics', undefined, signal),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  })
}

/**
 * The rolling window of pushed self-samples, oldest first.
 *
 * `queryFn` returns an empty array and is never expected to run more than once:
 * the SSE handler owns this key. It exists only so a cold mount renders an empty
 * chart instead of a loading state, and so the key has a defined shape before
 * the first frame lands.
 */
export function useSelfMetricsHistory(): SelfMetricsDto[] {
  const query = useQuery({
    queryKey: SELF_METRICS_KEY,
    queryFn: () => [] as SelfMetricsDto[],
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
  return query.data ?? []
}

/* ------------------------------------------------------------------ *
 * Derivations
 *
 * Shared by the rail chip and the route so the dot in the corner and the page
 * it links to can never tell different stories.
 * ------------------------------------------------------------------ */

export type SystemTone = 'ok' | 'warn' | 'danger' | 'neutral'

export interface SystemHealth {
  tone: SystemTone
  /** One or two words for the chip. */
  label: string
  /** A sentence for the tooltip, naming the worst thing found. */
  detail: string
}

/**
 * Event-loop thresholds, in ms.
 *
 * These are the numbers that matter here, and the reason is specific to this
 * process: its real ceilings are all single-threaded, so saturation shows up as
 * a loop that stops turning while RSS and container CPU still look mild. A
 * quarter-second of lag is already visible as a console that feels sticky; a
 * full second means requests are queueing behind something synchronous.
 */
const LOOP_WARN_MS = 250
const LOOP_DANGER_MS = 1_000

/**
 * Memory is judged against the PLAN LIMIT, not against a fixed number of GB.
 *
 * The API sits near 1 GB against a 24 GB ceiling, so an absolute threshold would
 * either never fire or fire constantly depending on the plan. A ratio keeps
 * meaning the same thing after a resize.
 */
const MEMORY_WARN_RATIO = 0.75
const MEMORY_DANGER_RATIO = 0.9

/** Beyond this, a pushed sample is old enough that the stream is likely down. */
const SELF_STALE_MS = 15_000

export function systemHealth(
  self: SelfMetricsDto | null,
  service: ServiceMetricsDto | null,
  railwayError: string | null,
  now = Date.now()
): SystemHealth {
  if (!self) {
    return { tone: 'neutral', label: 'No data', detail: 'No metrics have arrived yet.' }
  }

  // A stale sample outranks everything below it: the numbers may look perfect
  // and simply be a minute old, and reporting them as healthy is worse than
  // saying the wire is quiet.
  if (now - self.at > SELF_STALE_MS) {
    return {
      tone: 'neutral',
      label: 'Stale',
      detail: 'No live sample recently — the event stream is probably disconnected.',
    }
  }

  const limit = service?.memoryLimitGb ?? null
  const memoryRatio = limit && limit > 0 ? self.rssGb / limit : null

  if (self.eventLoopDelayMs >= LOOP_DANGER_MS) {
    return {
      tone: 'danger',
      label: 'Blocked',
      detail: `Event loop stalled ${Math.round(self.eventLoopDelayMs)}ms — requests are queueing behind synchronous work.`,
    }
  }
  if (memoryRatio !== null && memoryRatio >= MEMORY_DANGER_RATIO) {
    return {
      tone: 'danger',
      label: 'Memory',
      detail: `Using ${self.rssGb.toFixed(2)} GB of a ${limit} GB limit.`,
    }
  }
  if (self.eventLoopDelayMs >= LOOP_WARN_MS) {
    return {
      tone: 'warn',
      label: 'Busy',
      detail: `Event loop lagging ${Math.round(self.eventLoopDelayMs)}ms.`,
    }
  }
  if (memoryRatio !== null && memoryRatio >= MEMORY_WARN_RATIO) {
    return {
      tone: 'warn',
      label: 'Memory',
      detail: `Using ${self.rssGb.toFixed(2)} GB of a ${limit} GB limit.`,
    }
  }
  /*
   * A Railway failure is a WARNING, never a danger.
   *
   * It means the console has lost sight of the other services — it says nothing
   * at all about whether this process is healthy, and the live half of the panel
   * is still perfectly good. Ranking it below the real signals keeps a missing
   * token from painting the rail red on a box that is completely fine.
   */
  if (railwayError !== null) {
    return { tone: 'warn', label: 'Partial', detail: railwayError }
  }

  return { tone: 'ok', label: 'Healthy', detail: 'Event loop responsive, memory well inside its limit.' }
}

/** The Railway service the answering process runs as, when it named one. */
export function selfService(snapshot: SystemSnapshotDto | undefined): ServiceMetricsDto | null {
  if (!snapshot?.selfServiceId) return null
  return snapshot.railway.services.find((s) => s.serviceId === snapshot.selfServiceId) ?? null
}

/**
 * The freshest self-sample available: the pushed one when the stream is up,
 * otherwise the seed the poll carried.
 *
 * Both are the same shape, so preferring the stream is purely about age — and
 * falling back to the seed is what makes the panel useful with the stream down.
 */
export function latestSelf(
  history: SelfMetricsDto[],
  snapshot: SystemSnapshotDto | undefined
): SelfMetricsDto | null {
  return history.at(-1) ?? snapshot?.self ?? null
}
